/**
 * Helper utilities for performing direct document mutations on the Zustand store.
 *
 * These helpers encapsulate the common boilerplate needed when mutating document state:
 * 1. Get the current document
 * 2. Apply the mutation via withHistoryUpdate (for Immer patch generation)
 * 3. Set the new document in the store
 * 4. Process undo/redo if applicable
 * 5. Notify effect services about the processed action
 *
 * This allows direct named methods on the store to replace the Redux-style
 * dispatch(actionCreator(...)) → handler chain pattern.
 */

import {withHistoryUpdate} from "@buildwithflux/core";
import type {IDocumentData, IUserData} from "@buildwithflux/models";

import type {DocumentServices, ActionEventBus} from "./serviceNotifier";
import {notifyServicesOfAction} from "./serviceNotifier";
import type {CurrentUserProvider, DocumentStoreState, DocumentActionWithOptionalPayload} from "./types";
import {processUndoRedoInStore, type UndoRedoStackOperations} from "./undoRedoProcessing";
import type {UndoRedoStore} from "./undoRedoStore";

/**
 * Builds a partial state update that sets the document and promotes all its
 * fields to top-level store keys. When document is null, the promoted fields
 * are set to undefined so selectors see a consistent "no document" state.
 *
 * @param document The new document data, or null to clear.
 * @param previousDocument The previous document whose promoted keys need clearing.
 *   Required when document is null to avoid stale top-level fields.
 */
export function buildDocumentStateUpdate(
    document: IDocumentData | null,
    previousDocument?: IDocumentData | null,
): Partial<DocumentStoreState> {
    if (document === null) {
        const result: Record<string, unknown> = {document: null};
        if (previousDocument) {
            for (const key of Object.keys(previousDocument)) {
                result[key] = undefined;
            }
        }
        return result as Partial<DocumentStoreState>;
    }
    return {...document, document};
}

/**
 * Metadata for a document mutation, used to construct the action object
 * that subscribers (ActionRecordService, DocumentSaveService, PatchHandlerService) expect.
 */
export interface DocumentMutationMetadata {
    actionType: string;
    canUndo: boolean;
    shouldGenerateActionRecord: boolean;
    updatesDocumentTimestamp: boolean;
    additionalPayload?: Record<string, unknown>;
}

/**
 * Store primitives needed by performDocumentMutation.
 * These are passed in from the store's (set, get) closures.
 *
 * All dynamic fields use lazy getters so this object can be created once per store
 * and reused across all action methods, eliminating per-action object allocation.
 */
export interface DocumentStorePrimitives {
    getDocument: () => IDocumentData | null;
    setDocument: (document: IDocumentData | null, actionType: string) => void;
    notifyServices: (action: DocumentActionWithOptionalPayload) => void;
    getStackOps: () => UndoRedoStackOperations;
    getCurrentUser: () => IUserData | undefined;
    undoRedoStore: UndoRedoStore;
}

/**
 * Performs a document mutation with full undo/redo and subscriber notification support.
 *
 * This is the core helper that direct store methods use instead of going through
 * the dispatch → handler chain. It preserves all existing behavior:
 * - Immer patch generation via withHistoryUpdate
 * - Undo/redo stack management
 * - Service notification via notifyServices
 *
 * @param primitives - Store primitives (getDocument, setDocument, etc.)
 * @param mutate - The Immer mutation function to apply to the document draft
 * @param metadata - Action metadata for undo/redo and subscriber compatibility
 */
export function performDocumentMutation(
    primitives: DocumentStorePrimitives,
    mutate: (draft: IDocumentData) => void,
    metadata: DocumentMutationMetadata,
): void {
    const document = primitives.getDocument();
    if (!document) {
        return;
    }

    const newDocument = withHistoryUpdate(document, mutate);

    const action: DocumentActionWithOptionalPayload = {
        type: metadata.actionType,
        currentUser: primitives.getCurrentUser(),
        payload: {
            ...metadata.additionalPayload,
            canUndo: metadata.canUndo,
            shouldGenerateActionRecord: metadata.shouldGenerateActionRecord,
            updatesDocumentTimestamp: metadata.updatesDocumentTimestamp,
        },
    };

    if (newDocument !== document) {
        primitives.setDocument(newDocument, metadata.actionType);
        processUndoRedoInStore(action, newDocument, primitives.getStackOps());
    }

    primitives.notifyServices(action);
}

/**
 * Creates a DocumentStorePrimitives object from the store's (set, get) closures
 * and injected dependencies.
 *
 * This object should be created once per store instance and reused across all action
 * methods. All dynamic state (currentUser, stackOps) is accessed lazily via getters,
 * so the object itself is stable and doesn't need to be recreated per action.
 */
export function createStorePrimitives(
    set: (state: Partial<DocumentStoreState>, replace?: boolean, actionType?: string) => void,
    get: () => DocumentStoreState,
    undoRedoStore: UndoRedoStore,
    currentUserService: CurrentUserProvider | undefined,
    services: DocumentServices,
    actionEventBus: ActionEventBus | undefined,
): DocumentStorePrimitives {
    return {
        getDocument: () => get().document,
        setDocument: (document, actionType) => {
            set(buildDocumentStateUpdate(document, get().document), false, `document/${actionType}`);
        },
        notifyServices: (action) => {
            notifyServicesOfAction(action, services, actionEventBus);
        },
        getStackOps: () => {
            const undoRedoState = undoRedoStore.getState();
            return {
                pushToUndoStack: undoRedoState.pushToUndoStack,
                pushToRedoStack: undoRedoState.pushToRedoStack,
                clearRedoStack: undoRedoState.clearRedoStack,
            };
        },
        getCurrentUser: () => currentUserService?.getCurrentUser(),
        undoRedoStore,
    };
}
